import { useCallback, useMemo, useRef, useState } from 'react';
import { getVerifyMatchTargetKey } from './get-verify-match-target-key';
import { VerifyMatchDialogNavigationState, VerifyMatchTarget } from './verify-match-types';

export interface UseVerifyMatchDialogResult {
  open: boolean;
  target: VerifyMatchTarget | null;
  openAt: (next: VerifyMatchTarget) => void;
  close: () => void;
  navigation: VerifyMatchDialogNavigationState;
}

/**
 * Owns a single verify-match dialog for a list of rows.
 * Keeps the last target after close so MUI can finish the exit transition.
 */
export function useVerifyMatchDialog(targets: VerifyMatchTarget[]): UseVerifyMatchDialogResult {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<VerifyMatchTarget | null>(null);
  const targetsRef = useRef(targets);
  targetsRef.current = targets;

  const selectedKey = target ? getVerifyMatchTargetKey(target) : null;
  const index = selectedKey
    ? targets.findIndex((item) => getVerifyMatchTargetKey(item) === selectedKey)
    : -1;
  const resolvedTarget = index >= 0 ? targets[index] : target;
  const hasPrev = index > 0;
  const hasNext = index >= 0 && index < targets.length - 1;

  const openAt = useCallback((next: VerifyMatchTarget) => {
    setTarget(next);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const goPrev = useCallback(() => {
    setTarget((current) => {
      if (!current) {
        return current;
      }

      const list = targetsRef.current;
      const currentIndex = list.findIndex(
        (item) => getVerifyMatchTargetKey(item) === getVerifyMatchTargetKey(current),
      );
      if (currentIndex <= 0) {
        return current;
      }

      return list[currentIndex - 1];
    });
  }, []);

  const goNext = useCallback(() => {
    setTarget((current) => {
      if (!current) {
        return current;
      }

      const list = targetsRef.current;
      const currentIndex = list.findIndex(
        (item) => getVerifyMatchTargetKey(item) === getVerifyMatchTargetKey(current),
      );
      if (currentIndex < 0 || currentIndex >= list.length - 1) {
        return current;
      }

      return list[currentIndex + 1];
    });
  }, []);

  const navigation = useMemo<VerifyMatchDialogNavigationState>(
    () => ({
      onPrev: goPrev,
      onNext: goNext,
      hasPrev,
      hasNext,
      current: index >= 0 ? index + 1 : 0,
      total: targets.length,
    }),
    [goPrev, goNext, hasPrev, hasNext, index, targets.length],
  );

  return {
    open,
    target: resolvedTarget,
    openAt,
    close,
    navigation,
  };
}
